import React, { useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { GQLParty } from '@jewelutions/core-crm-server-api-client-domain';

import { JwlTable, Chip, LoadingSpinner, ErrorComponent } from '../../../../components';

import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import { ProducerNavigation } from '../../navigation/ProducerNavigation';
import { Link } from '@material-ui/core';

interface IProps {
  party: GQLParty;
}

export const DeliveryUnitSaleCatalogList: React.FC<IProps> = ({ party }: IProps) => {
  const history = useHistory();
  const deliveryUnitId = ProducerNavigation.deliveryUnitSaleCatalogList.paramsFromPath(useRouteMatch())
    .deliveryUnitId;
  const [errorComponentState, setErrorComponentState] = useState(false);

  const {
    data: saleCatalogsData,
    loading: saleCatalogsLoading,
    error: saleCatalogsError,
    refetch: refetchSaleCatalogs
  } = GQLOperationBuilder.deliveryUnitSaleCatalog.getDeliveryUnitSaleCatalogs(
    {
      producerPartyId: party.id,
      deliveryUnitId: deliveryUnitId
    },
    { fetchPolicy: 'network-only' }
  );

  const {
    data: requestsData,
    loading: requestsLoading,
    error: requestsError,
    refetch: refetchRequests
  } = GQLOperationBuilder.request.getPlatformProducerContractChangeRequestByProducer(
    {
      producerPartyId: party.id,
      deliveryUnitId: deliveryUnitId,
      completed: false
    },
    { fetchPolicy: 'network-only' }
  );

  if (saleCatalogsLoading || requestsLoading) return <LoadingSpinner />;

  if (saleCatalogsError && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={saleCatalogsError?.stack}
        callback={refetchSaleCatalogs}
        message={saleCatalogsError.message}
      />
    );

  const requests = GQLOperationBuilder.request.requestArrayFromResponse(requestsData);
  const catalogs = GQLOperationBuilder.deliveryUnitSaleCatalog.deliveryUnitSaleCatalogWithPlatformProducerContractArrayFromResponse(
    saleCatalogsData
  );

  const getDeliveryUnitSaleCatalogWithPlatformProducerContractsRows = () => {
    return catalogs.map(saleCatalog => {
      return {
        id: saleCatalog.deliveryUnitSaleCatalog.id,
        cells: [
          saleCatalog.deliveryUnitSaleCatalog.name.en,
          saleCatalog.deliveryUnitSaleCatalog.description.en,
          saleCatalog.deliveryUnitSaleCatalog.inventoryCatalog.pieceInventorys.length,
          saleCatalog.deliveryUnitSaleCatalog.inventoryCatalog.accessoryInventorys.length,
          saleCatalog.deliveryUnitSaleCatalog.pricingCatalogs.map(c => c.currency).join(','),
          <Chip
            size="small"
            mr={1}
            mb={1}
            label={saleCatalog.deliveryUnitSaleCatalogPublished ? 'YES' : 'NO'}
            $active={saleCatalog.deliveryUnitSaleCatalogPublished}
          />,
          <Chip
            size="small"
            mr={1}
            mb={1}
            label={saleCatalog.platformProducerContractActive ? 'YES' : 'NO'}
            $active={saleCatalog.platformProducerContractActive}
          />,
          new Date(Date.parse(saleCatalog.deliveryUnitSaleCatalog.updatedAt)).toLocaleString()
        ]
      };
    });
  };

  const navigateToSaleCatalogDetails = (deliveryUnitSaleCatalogId: string) => {
    history.push(
      ProducerNavigation.deliveryUnitSaleCatalogDetail.pathFromParams({
        deliveryUnitId: deliveryUnitId,
        deliveryUnitSaleCatalogId: deliveryUnitSaleCatalogId
      })
    );
  };

  const requestsRows = () => {
    return requests.map(request => {
      const requestPreviousCatalog = catalogs.find(
        c =>
          c.deliveryUnitSaleCatalog.id ===
          request.request.platformProducerContractChange?.previousContract.deliveryUnitSaleCatalogId
      );
      const requestNextCatalog = catalogs.find(
        c =>
          c.deliveryUnitSaleCatalog.id ===
          request.request.platformProducerContractChange?.nextContract.deliveryUnitSaleCatalogId
      );

      return {
        id: request.id,
        cells: [
          <Link
            onClick={() => {
              history.push(
                ProducerNavigation.deliveryUnitSaleCatalogDetail.pathFromParams({
                  deliveryUnitId: request.request.platformProducerContractChange!.previousContract
                    .producerDeliveryUnit.id,
                  deliveryUnitSaleCatalogId: request.request.platformProducerContractChange!.previousContract
                    .deliveryUnitSaleCatalogId
                })
              );
            }}
          >
            {requestPreviousCatalog
              ? `${requestPreviousCatalog.deliveryUnitSaleCatalog.name.en}`
              : `Previous sale catalog`}
          </Link>,
          <Link
            onClick={() => {
              history.push(
                ProducerNavigation.deliveryUnitSaleCatalogDetail.pathFromParams({
                  deliveryUnitId: request.request.platformProducerContractChange!.nextContract
                    .producerDeliveryUnit.id,
                  deliveryUnitSaleCatalogId: request.request.platformProducerContractChange!.nextContract
                    .deliveryUnitSaleCatalogId
                })
              );
            }}
          >
            {requestNextCatalog
              ? `${requestNextCatalog.deliveryUnitSaleCatalog.name.en}`
              : `Next sale catalog`}
          </Link>,
          new Date(Date.parse(request.createdAt)).toLocaleString(),
          new Date(Date.parse(request.updatedAt)).toLocaleString(),
          new Date(Date.parse(request.validUntil)).toLocaleString(),
          <Chip
            size="small"
            mr={1}
            mb={1}
            label={`${request.finishedAt ? 'YES' : 'PENDING'}`}
            $active={request.finishedAt && request.activities[0].approval?.accepted === true}
          />
        ]
      };
    });
  };

  return (
    <React.Fragment>
      {requests.length > 0 && (
        <JwlTable
          title="Open requests for contract change"
          description={`The delivery unit ${party.name} pending requests for a producer <-> platform contract change. Until a contract change request is accepted the existing contract still applies.`}
          columns={[
            'Previous catalog',
            'Next catalog',
            'Request created at',
            'Request updated at',
            'Request valid until',
            'State'
          ]}
          rows={requestsRows()}
        />
      )}
      <JwlTable
        title="Delivery Unit sale catalogs list"
        description={`The delivery unit sale catalogs of the current delivery unit of producer ${party.name}. The sala catalog contains a complete description of the commitments of the production unit at a specific time in regards with the pieces / accessories inventory & production prices. Only one catalog can be part of an active contract.`}
        columns={[
          'Name',
          'Description',
          'Inventory Pieces',
          'Inventory Accessories',
          'Currency',
          'Delivery Unit Sale Catalog Published',
          'Platform Producer Contract Active',
          'Updated At'
        ]}
        rows={getDeliveryUnitSaleCatalogWithPlatformProducerContractsRows()}
        onTableRowClick={navigateToSaleCatalogDetails}
      />
    </React.Fragment>
  );
};
